<script>
	import { onMount } from 'svelte';
	import { getApi } from '../../service/api';
	import { parseSleepStage, parseLocalTimeMs } from './components/DataParser.js';
	import SleepStageChart from './components/SleepStageChart.svelte';
	import LightControlChart from './components/LightControlChart.svelte';

	let id = '';
	$: {
		const path = window.location.pathname;
		const match = path.match(/\/samsung\/lights\/(\d+)/);
		if (match) id = match[1];
	}

	let loading = true;
	let sleepData = null;
	let rawSleepStage = null;
	let sleepwaveLights = [];
	let samsungLights = [];
	let ourStageData = [];

	async function fetchLightsData() {
		if (!id) return;
		loading = true;
		try {
			const res = await getApi(`/samsung/lights/${id}`);
			if (res && res.resultCode === 1000) {
				sleepData = res.data?.sleepData;
				rawSleepStage = res.data?.rawSleepStage;
				sleepwaveLights = res.data?.sleepwaveLights || [];
				samsungLights = res.data?.samsungLights || [];

				// 수면 단계 파싱 (차트 배경용)
				if (rawSleepStage && sleepData?.timeStart) {
					const startTime = parseLocalTimeMs(sleepData.timeStart);
					ourStageData = parseSleepStage(rawSleepStage, startTime);
				}
			}
		} catch (e) {
			console.error('조명 데이터 로드 실패:', e);
		}
		loading = false;
	}

	function formatDate(dateStr) {
		if (!dateStr) return '';
		return new Date(dateStr).toLocaleDateString('ko-KR');
	}

	function formatDateTime(dateStr) {
		if (!dateStr) return '-';
		const d = new Date(parseLocalTimeMs(dateStr));
		return d.toLocaleString('ko-KR', {
			month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit'
		});
	}

	function parseLightRawData(rawData) {
		try {
			return JSON.parse(rawData);
		} catch {
			return {};
		}
	}

	onMount(() => {
		fetchLightsData();
	});
</script>

<div class="container-xxl flex-grow-1 container-p-y">
	<div class="d-flex align-items-center mb-4">
		<a href="/samsung/sessions" class="btn btn-sm btn-outline-secondary me-3">
			<i class="bx bx-arrow-back"></i>
		</a>
		<h4 class="fw-bold py-3 mb-0">
			조명 제어 이력
			{#if sleepData}
				<span class="text-muted fs-6 ms-2">
					#{id} | {formatDate(sleepData.ymd)} | User #{sleepData.idUser}
				</span>
			{/if}
		</h4>
	</div>

	{#if loading}
		<div class="text-center py-5">
			<div class="spinner-border" role="status"></div>
			<p class="mt-2 text-muted">데이터 로딩중...</p>
		</div>
	{:else}
		<!-- 수면 단계 배경 -->
		{#if ourStageData.length > 0}
			<SleepStageChart ourData={ourStageData} samsungData={[]} title="수면 단계 (배경 참조)" />
		{/if}

		<!-- 조명 제어 차트 -->
		<LightControlChart
			{sleepwaveLights}
			{samsungLights}
		/>

		<!-- Sleepwave 조명 제어 테이블 -->
		<div class="card mb-4">
			<div class="card-header">
				<h5 class="card-title mb-0">Sleepwave 조명 제어 상세</h5>
			</div>
			<div class="card-body">
				{#if sleepwaveLights.length === 0}
					<div class="text-center text-muted py-3">데이터 없음</div>
				{:else}
					{#each sleepwaveLights as lightGroup}
						<h6 class="mb-2">
							{lightGroup.device.deviceName || 'Unknown'}
							<small class="text-muted">({lightGroup.device.deviceType || '-'})</small>
						</h6>
						<div class="table-responsive mb-3">
							<table class="table table-sm table-bordered">
								<thead class="table-light">
									<tr>
										<th>시간</th>
										<th>Power</th>
										<th>밝기</th>
										<th>색온도</th>
										<th>색상</th>
										<th>Speed</th>
										<th>Fade</th>
									</tr>
								</thead>
								<tbody>
									{#each lightGroup.controls as ctrl}
										{@const raw = parseLightRawData(ctrl.rawData)}
										<tr>
											<td>{formatDateTime(ctrl.timestamp)}</td>
											<td>
												<span class="badge {raw.power === 'on' ? 'bg-success' : 'bg-secondary'}">
													{raw.power || '-'}
												</span>
											</td>
											<td>{raw.brightness || '-'}</td>
											<td>{raw.colorTemp || raw.color_temp || '-'}</td>
											<td>
												{#if raw.color}
													<span class="d-inline-block rounded-circle me-1" style="width:12px;height:12px;background:{raw.color};vertical-align:middle;"></span>
													{raw.color}
												{:else}
													-
												{/if}
											</td>
											<td>{raw.speed || '-'}</td>
											<td>{raw.fade || '-'}</td>
										</tr>
									{/each}
								</tbody>
							</table>
						</div>
					{/each}
				{/if}
			</div>
		</div>

		<!-- Samsung 조명 제어 테이블 -->
		<div class="card mb-4">
			<div class="card-header">
				<h5 class="card-title mb-0">Samsung Health 조명 제어 상세</h5>
			</div>
			<div class="card-body">
				{#if samsungLights.length === 0}
					<div class="text-center text-muted py-3">데이터 없음</div>
				{:else}
					{#each samsungLights as lightGroup}
						<h6 class="mb-2">
							{lightGroup.device.deviceName || 'Unknown'}
							<small class="text-muted">({lightGroup.device.deviceType || '-'})</small>
						</h6>
						<div class="table-responsive mb-3">
							<table class="table table-sm table-bordered">
								<thead class="table-light">
									<tr>
										<th>시간</th>
										<th>Power</th>
										<th>밝기</th>
										<th>색온도</th>
										<th>색상</th>
										<th>Speed</th>
										<th>Fade</th>
									</tr>
								</thead>
								<tbody>
									{#each lightGroup.controls as ctrl}
										<tr>
											<td>{new Date(parseLocalTimeMs(ctrl.timestamp)).toLocaleTimeString('ko-KR')}</td>
											<td>
												<span class="badge {ctrl.power === 'on' ? 'bg-success' : 'bg-secondary'}">
													{ctrl.power || '-'}
												</span>
											</td>
											<td>{ctrl.brightness || '-'}</td>
											<td>{ctrl.colorTemp || '-'}</td>
											<td>
												{#if ctrl.color}
													<span class="d-inline-block rounded-circle me-1" style="width:12px;height:12px;background:{ctrl.color};vertical-align:middle;"></span>
													{ctrl.color}
												{:else}
													-
												{/if}
											</td>
											<td>{ctrl.speed || '-'}</td>
											<td>{ctrl.fade || '-'}</td>
										</tr>
									{/each}
								</tbody>
							</table>
						</div>
					{/each}
				{/if}
			</div>
		</div>
	{/if}
</div>
